import api from './api';

/**
 * Instagram graphic generator supporting both Feed (1080×1080) and Story
 * (1080×1920) formats.
 *
 * Composition (top → bottom):
 *  - Background: event main image, "cover" fit
 *  - Black overlay 60% opacity
 *  - TOP CENTER  → venue name, pale gold, uppercase, letter-spaced
 *  - ABS CENTER  → event title, optical white, large bold, multi-line wrapped
 *  - BELOW TITLE → date + start time, optical white
 *  - BOTTOM      → "romenightlife.it" domain, small white
 *
 * Style guideline: minimalist, high-end, exclusively modern sans-serif (Work Sans).
 */

// Per-format layout constants (positions/sizes tuned for each canvas).
// Consumers pick a format via `renderIgCanvas({ format: 'feed' | 'story' })`.
const FORMATS = {
  feed: {
    w: 1080,
    h: 1080,
    venueY: 150,
    separatorY: 190,
    venueSize: 42,
    titleStartSize: 108,
    titleMinSize: 46,
    titleLineHeightFactor: 1.06,
    dateSize: 44,
    dateOffsetFromTitle: 62,
    domainY: 1020,
    domainSize: 26,
  },
  story: {
    w: 1080,
    h: 1920,
    venueY: 260,
    separatorY: 310,
    venueSize: 48,
    titleStartSize: 130,
    titleMinSize: 56,
    titleLineHeightFactor: 1.08,
    dateSize: 52,
    dateOffsetFromTitle: 80,
    domainY: 1830,
    domainSize: 30,
  },
};

const DEFAULT_FORMAT = 'feed';

// Brand palette
const PALE_GOLD = '#E6C77E';
const OPTICAL_WHITE = '#FAFAFA';
const PURE_WHITE = '#FFFFFF';

const DOMAIN = 'romenightlife.it';

// Italian weekday + month names (no external deps)
const GIORNI = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

function formatItalianDate(dateStr) {
  // Expected input: "YYYY-MM-DD" (HTML date input)
  if (!dateStr) return '';
  try {
    const [y, m, d] = String(dateStr).split('-').map((n) => parseInt(n, 10));
    if (!y || !m || !d) return dateStr;
    const dt = new Date(y, m - 1, d);
    const giorno = GIORNI[dt.getDay()];
    const mese = MESI[m - 1];
    return `${giorno} ${d} ${mese}`;
  } catch {
    return dateStr;
  }
}

function isSameOrigin(url) {
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Load an image into an HTMLImageElement, routing external URLs through
 * the authenticated admin image proxy to avoid CORS canvas tainting.
 */
async function loadImage(url) {
  if (!url) return null;

  const isData = url.startsWith('data:');
  const isLocal = isData || isSameOrigin(url) || url.startsWith('/');

  let src = url;
  let blobUrl = null;

  if (!isLocal) {
    // Route through backend proxy (auth header attached via axios interceptor)
    const resp = await api.get('/admin/image-proxy', {
      params: { url },
      responseType: 'blob',
    });
    blobUrl = URL.createObjectURL(resp.data);
    src = blobUrl;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    // crossOrigin only needed for actual cross-origin URLs (not blob/data)
    if (!isData && !blobUrl && !isLocal) img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (blobUrl) {
        // Defer revoke so canvas can finish reading pixels
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      }
      resolve(img);
    };
    img.onerror = () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      reject(new Error('image_load_failed'));
    };
    img.src = src;
  });
}

/**
 * Ensure Work Sans is fully loaded before painting on canvas.
 * Without this, the first generation can fall back to a system font.
 */
async function ensureFontsReady() {
  if (!document.fonts) return;
  try {
    // Force a load for the weights we use
    await Promise.all([
      document.fonts.load('600 48px "Work Sans"'),
      document.fonts.load('700 110px "Work Sans"'),
      document.fonts.load('500 52px "Work Sans"'),
      document.fonts.load('500 28px "Work Sans"'),
    ]);
    await document.fonts.ready;
  } catch {
    /* non-fatal */
  }
}

/**
 * Draw an image with "cover" fit inside (0,0,w,h), centered.
 */
function drawCover(ctx, img, w, h) {
  const ratio = Math.max(w / img.width, h / img.height);
  const dw = img.width * ratio;
  const dh = img.height * ratio;
  const x = (w - dw) / 2;
  const y = (h - dh) / 2;
  ctx.drawImage(img, x, y, dw, dh);
}

/**
 * Word-wrap text to fit within maxWidth. Returns the array of lines.
 * Caller is responsible for setting ctx.font before calling.
 */
function wrapText(ctx, text, maxWidth) {
  const words = String(text || '').trim().split(/\s+/);
  if (words.length === 0) return [];
  const lines = [];
  let current = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const candidate = `${current} ${words[i]}`;
    if (ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
    } else {
      lines.push(current);
      current = words[i];
    }
  }
  lines.push(current);
  return lines;
}

/**
 * Auto-shrink font size until the wrapped title fits within maxWidth and
 * maxLines. Returns { lines, fontSize }.
 */
function fitTitle(ctx, text, maxWidth, startSize, minSize, maxLines) {
  let size = startSize;
  while (size >= minSize) {
    ctx.font = `700 ${size}px "Work Sans", system-ui, sans-serif`;
    const lines = wrapText(ctx, text, maxWidth);
    if (lines.length <= maxLines) {
      // Also verify the longest line truly fits
      const tooWide = lines.some((l) => ctx.measureText(l).width > maxWidth);
      if (!tooWide) return { lines, fontSize: size };
    }
    size -= 6;
  }
  ctx.font = `700 ${minSize}px "Work Sans", system-ui, sans-serif`;
  return { lines: wrapText(ctx, text, maxWidth).slice(0, maxLines), fontSize: minSize };
}

/**
 * Trigger a download of a canvas as PNG.
 */
function downloadCanvas(canvas, filename) {
  return new Promise((resolve, reject) => {
    if (!canvas.toBlob) {
      try {
        const url = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        resolve();
      } catch (e) { reject(e); }
      return;
    }
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error('toBlob_failed')); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      resolve();
    }, 'image/png', 0.95);
  });
}

function safeFilename(s) {
  return String(s || 'evento')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'evento';
}

export { FORMATS as IG_FORMATS };
export const IG_FORMAT_KEYS = Object.keys(FORMATS);

/**
 * Build the IG graphic filename for a given event + format.
 */
export function igFilenameFor({ titolo, data, format = DEFAULT_FORMAT }) {
  const suffix = format === 'story' ? 'IG-story' : 'IG-feed';
  return `${safeFilename(titolo)}-${safeFilename(data)}-${suffix}.png`;
}

/**
 * Render the Instagram graphic on an offscreen canvas for a given format.
 * Returns { canvas, previewDataUrl, width, height }. `previewDataUrl` is a
 * lower-resolution JPEG dataURL suited for embedding in an <img> preview.
 *
 * @param {Object} params
 * @param {string} params.titolo
 * @param {string} params.data          - "YYYY-MM-DD"
 * @param {string} params.orario        - "HH:mm"
 * @param {string} params.venueName
 * @param {string} params.imageUrl
 * @param {'feed'|'story'} [params.format='feed']
 */
export async function renderIgCanvas({
  titolo,
  data,
  orario,
  venueName,
  imageUrl,
  format = DEFAULT_FORMAT,
}) {
  if (!imageUrl) throw new Error('Nessuna immagine disponibile per generare la locandina');

  const fmt = FORMATS[format] || FORMATS[DEFAULT_FORMAT];
  const W = fmt.w;
  const H = fmt.h;

  await ensureFontsReady();
  const img = await loadImage(imageUrl);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas non supportato');

  // 1) Background
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, W, H);
  if (img) drawCover(ctx, img, W, H);

  // 2) Semi-transparent black overlay (60%)
  ctx.fillStyle = 'rgba(0, 0, 0, 0.60)';
  ctx.fillRect(0, 0, W, H);

  // 3) Subtle vertical gradient for legibility
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, 'rgba(0,0,0,0.35)');
  grad.addColorStop(0.5, 'rgba(0,0,0,0.0)');
  grad.addColorStop(1, 'rgba(0,0,0,0.45)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const SAFE_PAD = 80;
  const MAX_TEXT_WIDTH = W - SAFE_PAD * 2;

  // 4) Venue name
  if (venueName) {
    ctx.fillStyle = PALE_GOLD;
    const spacedVenue = String(venueName).toUpperCase().split('').join('\u2009');
    let venueSize = fmt.venueSize;
    ctx.font = `600 ${venueSize}px "Work Sans", system-ui, sans-serif`;
    while (ctx.measureText(spacedVenue).width > MAX_TEXT_WIDTH && venueSize > 26) {
      venueSize -= 2;
      ctx.font = `600 ${venueSize}px "Work Sans", system-ui, sans-serif`;
    }
    ctx.fillText(spacedVenue, W / 2, fmt.venueY);
    ctx.fillStyle = PALE_GOLD;
    ctx.fillRect(W / 2 - 40, fmt.separatorY, 80, 2);
  }

  // 5) Title
  const titleFit = fitTitle(
    ctx,
    titolo || '',
    MAX_TEXT_WIDTH,
    fmt.titleStartSize,
    fmt.titleMinSize,
    4,
  );
  const titleLineHeight = Math.round(titleFit.fontSize * fmt.titleLineHeightFactor);
  const titleBlockHeight = titleFit.lines.length * titleLineHeight;
  const titleStartY = H / 2 - titleBlockHeight / 2 + titleLineHeight / 2;

  ctx.fillStyle = OPTICAL_WHITE;
  ctx.font = `700 ${titleFit.fontSize}px "Work Sans", system-ui, sans-serif`;
  titleFit.lines.forEach((line, i) => {
    ctx.fillText(line, W / 2, titleStartY + i * titleLineHeight);
  });

  // 6) Date + time
  const dateLine = [formatItalianDate(data), orario].filter(Boolean).join(' • ').toUpperCase();
  if (dateLine) {
    ctx.fillStyle = OPTICAL_WHITE;
    const spacedDate = dateLine.split('').join('\u2009');
    let dateSize = fmt.dateSize;
    ctx.font = `500 ${dateSize}px "Work Sans", system-ui, sans-serif`;
    while (ctx.measureText(spacedDate).width > MAX_TEXT_WIDTH && dateSize > 22) {
      dateSize -= 2;
      ctx.font = `500 ${dateSize}px "Work Sans", system-ui, sans-serif`;
    }
    const dateY =
      titleStartY +
      (titleFit.lines.length - 1) * titleLineHeight +
      titleLineHeight * 0.7 +
      fmt.dateOffsetFromTitle;
    ctx.fillText(spacedDate, W / 2, dateY);
  }

  // 7) Domain
  ctx.fillStyle = PURE_WHITE;
  ctx.font = `500 ${fmt.domainSize}px "Work Sans", system-ui, sans-serif`;
  const spacedDomain = DOMAIN.split('').join('\u2009');
  ctx.fillText(spacedDomain, W / 2, fmt.domainY);

  // Fast, low-weight JPEG for on-screen preview
  const previewDataUrl = canvas.toDataURL('image/jpeg', 0.82);
  return { canvas, previewDataUrl, width: W, height: H, format };
}

/**
 * Trigger a PNG download from an already-rendered canvas.
 */
export function downloadCanvasAsPng(canvas, filename) {
  return downloadCanvas(canvas, filename);
}

/**
 * Convenience wrapper: render + immediately download (defaults to Feed 1:1).
 * (Kept for backwards-compatibility with the "Genera Locandina IG" button.)
 */
export async function generateInstagramGraphic({ titolo, data, orario, venueName, imageUrl, format = DEFAULT_FORMAT }) {
  const { canvas } = await renderIgCanvas({ titolo, data, orario, venueName, imageUrl, format });
  await downloadCanvas(canvas, igFilenameFor({ titolo, data, format }));
}
